/**
 * KraftStories — SESSION ANALYTICS
 * ============================================================================
 * GA4 instrumentation for the two in-house systems that replaced Cowlendar and
 * the Givy gift-card product:
 *
 *   BOOKING  assets/se-picker.js  (window.SEPicker)
 *   GIFTING  snippets/gift-modal.liquid  (window.KraftGiftModal)
 *
 * Replaces assets/booking-journey.js, which watched `.cowlendar-root` and has
 * therefore emitted nothing since the Session Engine shipped.
 *
 * WHY A WRAPPER AND NOT EDITS TO THE SOURCE
 * -----------------------------------------
 * Neither system emits a single custom event and neither contains any analytics
 * call — verified by grepping both for dataLayer/gtag/dispatchEvent. So there is
 * nothing to listen to, and nothing to double-fire. Instrumentation therefore
 * has to come from outside, by wrapping the two public entry points:
 *
 *   window.SEPicker.mountSplit / .mount
 *   window.KraftGiftModal.open
 *
 * Both files are under active development (se-picker.js changed 2026-09-04,
 * gift-modal.liquid 2026-09-03). Keeping tracking in a separate file means
 * ongoing work on them cannot conflict with it. If the systems settle, the
 * better long-term shape is dispatchEvent calls inside the source and plain
 * listeners here — see docs/ga4-session-engine-tracking.md.
 *
 * THREE RULES THIS FILE OBEYS
 * ---------------------------
 * 1. NEVER patch window.fetch. Something in the theme already patches it and
 *    stashes the original on window.__nativeFetch; the inline booking path uses
 *    that original deliberately. A fetch patch would capture some bookings and
 *    silently miss others — the worst possible failure mode.
 * 2. NEVER re-fire an ecommerce event the theme already fires. Gift adds from a
 *    product page go through product-form-drawer.js, which dispatches
 *    theme:cart:add, which analytics-events.js already turns into add_to_cart.
 *    We detect that and stand down (see giftCartWatch).
 * 3. NEVER send personal data. Recipient name, recipient email, sender name and
 *    gift message are all PII. We send presence booleans and lengths only.
 *
 * HOW ECOMMERCE EVENTS ARE EMITTED
 * --------------------------------
 * Not by hand. When a booking or an off-product-page gift lands in the cart we
 * dispatch the theme's own `theme:cart:add` with the /cart/add.js response, and
 * analytics-events.js builds the correctly shaped add_to_cart from it. That
 * keeps one definition of an ecommerce item in the codebase instead of two.
 *
 * Non-ecommerce funnel events go through window.__kraftGa4Mirror, the theme's
 * sanctioned emit helper, which is already buffered against a late gtag.
 */

(function () {
  'use strict';

  if (window.__kraftSessionAnalyticsLoaded) return;
  window.__kraftSessionAnalyticsLoaded = true;

  var DEBUG = /[?&]ks_debug=1/.test(window.location.search);

  // ─── emit ──────────────────────────────────────────────────────────────────
  // __kraftGa4Mirror appears when analytics-events.js runs. This file may load
  // first, so queue until it exists rather than dropping the opening events of
  // the session — which are exactly the ones that describe entry.

  var queue = [];
  var flushTimer = null;

  function drain() {
    if (typeof window.__kraftGa4Mirror !== 'function') return false;
    while (queue.length) {
      var q = queue.shift();
      try { window.__kraftGa4Mirror(q[0], q[1]); } catch (e) { /* never break the page */ }
    }
    return true;
  }

  function emit(name, params) {
    var payload = params || {};
    payload.page_path = window.location.pathname;
    if (window.__kraft_page_category) payload.page_category = window.__kraft_page_category;
    if (window.__kraft_page_location_city) payload.page_location_city = window.__kraft_page_location_city;

    if (DEBUG) console.log('[session-analytics]', name, payload);

    queue.push([name, payload]);
    if (drain()) return;
    if (flushTimer) return;
    var tries = 0;
    flushTimer = setInterval(function () {
      if (drain() || ++tries > 60) { clearInterval(flushTimer); flushTimer = null; }
    }, 250);
  }

  function currency() { return window.__kraft_analytics_currency || 'USD'; }
  function money(cents) { return Math.round((Number(cents) || 0)) / 100; }

  // ─── shared helpers ────────────────────────────────────────────────────────

  function daysAhead(iso) {
    if (!iso) return null;
    var d = new Date(iso + 'T00:00:00');
    if (isNaN(d)) return null;
    var today = new Date(); today.setHours(0, 0, 0, 0);
    return Math.round((d - today) / 86400000);
  }

  // Bucketed so they are readable as GA4 dimensions without registering a
  // custom METRIC for every number. Raw values go alongside where useful.
  function dwellBucket(ms) {
    var s = ms / 1000;
    if (s < 5) return '0-5s';
    if (s < 15) return '5-15s';
    if (s < 30) return '15-30s';
    if (s < 60) return '30-60s';
    if (s < 180) return '1-3m';
    return '3m+';
  }

  function sessionParams(s) {
    if (!s) return {};
    return {
      session_id_engine: String(s.id || ''),
      session_date: s.local_date || '',
      session_time: s.local_time || '',
      session_venue: s.venue || '',
      session_is_virtual: /virtual|zoom|online/i.test(s.venue || '') ? 'yes' : 'no',
      seats_available: Number(s.seats_available) || 0,
      seats_taken: Number(s.seats_taken) || 0,
      days_ahead: daysAhead(s.local_date),
      scarcity: (Number(s.seats_available) || 0) <= 4 ? 'low' : 'normal'
    };
  }

  // ─── booking: entry-point attribution ──────────────────────────────────────
  // se-picker knows nothing about which CTA opened it. The old booking-journey
  // had a SOURCE_MAP; re-derive it here from the clicked element, in capture
  // phase so it runs before the binder's own capture listener stops propagation.

  var lastCtaSource = 'unknown';
  var lastCtaAt = 0;

  function ctaSource(el) {
    if (!el) return 'unknown';
    if (el.closest('[data-se-sticky], .sticky-add-to-cart, .mobile_booking_cta')) return 'sticky_bar';
    if (el.closest('.hero-buy-now-btn, [data-se-hero]')) return 'hero_button';
    if (el.closest('[data-analytics-product], .product-card, .lfpt-card')) return 'collection_card';
    if (el.closest('#se-widget, [data-add-to-cart-btn]')) return 'product_page';
    if (el.closest('.lfpt-buy-now-btn')) return 'location_tabs';
    return 'page_button';
  }

  document.addEventListener('click', function (e) {
    var t = e.target && e.target.closest
      ? e.target.closest('[data-se-book], .lfpt-buy-now-btn, .hero-buy-now-btn, .mobile_booking_cta a, [data-add-to-cart-btn]')
      : null;
    if (!t) return;
    lastCtaSource = ctaSource(t);
    lastCtaAt = Date.now();
    emit('booking_cta_click', {
      cta_source: lastCtaSource,
      item_handle: t.getAttribute('data-se-handle') || ''
    });
  }, true);

  function freshSource() {
    // Only trust the attribution if the click was recent — otherwise the modal
    // was opened by a URL param or a programmatic call.
    return (Date.now() - lastCtaAt < 10000) ? lastCtaSource : 'direct';
  }

  // ─── booking: wrap the picker ──────────────────────────────────────────────

  function instrumentPicker(SEP) {
    if (!SEP || SEP.__ksInstrumented) return;
    SEP.__ksInstrumented = true;

    ['mountSplit', 'mount'].forEach(function (fnName) {
      var original = SEP[fnName];
      if (typeof original !== 'function') return;

      SEP[fnName] = function (root, data, opts) {
        opts = opts || {};
        var mode = (fnName === 'mountSplit') ? 'modal' : 'inline';
        var exp = (data && data.experience) || {};
        var all = (data && data.sessions) || [];
        // mountSplit filters sold-out sessions out before render, so soldout
        // exposure is only computable here, from the raw payload.
        var bookable = all.filter(function (s) { return s.seats_available > 0; });

        var st = {
          opened: Date.now(),
          furthest: 'open',
          confirmed: false,
          sessionPicked: false,
          calendarOpened: false,
          guestsChanged: false,
          variantPicked: false,
          holdStarted: false,
          cartStarted: false,
          errored: false,
          source: freshSource()
        };

        var base = {
          picker_mode: mode,
          cta_source: st.source,
          experience_title: exp.title || '',
          sessions_total: all.length,
          sessions_bookable: bookable.length,
          sessions_soldout: all.length - bookable.length,
          has_variants: (opts.variants && opts.variants.length > 1) ? 'yes' : 'no',
          seats_locked: opts.lockSeats ? 'yes' : 'no',
          first_session_venue: (bookable[0] && bookable[0].venue) || ''
        };

        function step(name) { st.furthest = name; }

        emit('booking_open', base);

        // ── wrap the callbacks the picker hands back to the binder ──

        var origConfirm = opts.onConfirm;
        if (typeof origConfirm === 'function') {
          opts.onConfirm = function (sess, guests, answersFn, variantId) {
            st.confirmed = true;
            step('confirm');
            var seats = (typeof guests === 'number') ? guests : 1;
            var unitCents = (opts.variants && opts.variants.length && variantId)
              ? variantPrice(opts.variants, variantId)
              : (sess && sess.price_cents) || 0;

            emit('booking_confirm', Object.assign({}, base, sessionParams(sess), {
              seats: seats,
              variant_id: variantId ? String(variantId) : '',
              value: money(unitCents * seats),
              currency: currency(),
              picked_session: st.sessionPicked ? 'yes' : 'no_default',
              used_calendar: st.calendarOpened ? 'yes' : 'no',
              changed_guests: st.guestsChanged ? 'yes' : 'no',
              time_to_confirm_ms: Date.now() - st.opened,
              dwell_bucket: dwellBucket(Date.now() - st.opened)
            }));

            return origConfirm.apply(this, arguments);
          };
        }

        var origEmpty = opts.onEmpty;
        opts.onEmpty = function () {
          step('no_availability');
          emit('booking_no_availability', base);
          if (typeof origEmpty === 'function') return origEmpty.apply(this, arguments);
        };

        var origClose = opts.onClose;
        opts.onClose = function () {
          closeOut('x_button');
          if (typeof origClose === 'function') return origClose.apply(this, arguments);
        };

        var closed = false;
        function closeOut(reason) {
          if (closed) return;
          closed = true;
          if (st.confirmed) return; // a confirmed booking is not an abandonment
          emit('booking_abandon', Object.assign({}, base, {
            close_reason: reason,
            furthest_step: st.furthest,
            picked_session: st.sessionPicked ? 'yes' : 'no',
            opened_calendar: st.calendarOpened ? 'yes' : 'no',
            dwell_ms: Date.now() - st.opened,
            dwell_bucket: dwellBucket(Date.now() - st.opened)
          }));
        }

        var out = original.apply(this, arguments);

        // ── delegated listeners on the picker's stable ids ──
        // These containers are re-rendered constantly, so delegate on the
        // container (stable) rather than binding to rows (destroyed on repaint).

        try {
          var rows = root.querySelector('#sps-rows');
          var calSlots = root.querySelector('#cal-slots');
          var calGrid = root.querySelector('#cal-grid');

          function onPick(method) {
            return function (e) {
              var row = e.target && e.target.closest ? e.target.closest('.srow, .cal-cell') : null;
              if (!row) return;
              st.sessionPicked = true;
              step('session_selected');
              var sid = row.getAttribute('data-id');
              var picked = sid ? bookable.filter(function (s) { return String(s.id) === String(sid); })[0] : null;
              emit('booking_session_select', Object.assign({}, base, sessionParams(picked), {
                select_method: method
              }));
            };
          }
          if (rows) rows.addEventListener('click', onPick('quick_row'));
          if (calSlots) calSlots.addEventListener('click', onPick('calendar_slot'));
          if (calGrid) calGrid.addEventListener('click', onPick('calendar_day'));

          var more = root.querySelector('#sps-more');
          if (more) more.addEventListener('click', function () {
            if (st.calendarOpened) return;
            st.calendarOpened = true;
            step('calendar_opened');
            emit('booking_calendar_open', base);
          });

          var gctl = root.querySelector('.gctl');
          if (gctl) gctl.addEventListener('click', function (e) {
            var b = e.target && e.target.closest ? e.target.closest('[data-d]') : null;
            if (!b) return;
            st.guestsChanged = true;
            step('guests_changed');
            emit('booking_guests_change', Object.assign({}, base, {
              direction: b.getAttribute('data-d') === '1' ? 'increase' : 'decrease'
            }));
          });

          var vp = root.querySelector('#sps-vp');
          if (vp) vp.addEventListener('click', function (e) {
            var o = e.target && e.target.closest ? e.target.closest('.sps-dd-opt') : null;
            if (!o) return;
            st.variantPicked = true;
            step('variant_selected');
            emit('booking_variant_select', base);
          });

          // Gate failures. The picker shows the error by unhiding #sps-err and
          // adding .bad to the gate; watch the checkboxes for the positive case
          // and spsError for the negative — see the wrapper below.
          ['#sps-gate-cb', '#sps-wgate-cb'].forEach(function (sel) {
            var cb = root.querySelector(sel);
            if (!cb) return;
            cb.addEventListener('change', function () {
              if (!cb.checked) return;
              emit('booking_gate_accept', Object.assign({}, base, {
                gate_type: sel === '#sps-gate-cb' ? 'age_14_plus' : 'waiver'
              }));
            });
          });

          // ── progress + error funnels ──
          // spsBusy/spsIdle/spsError are assigned as own properties on the mount
          // container by every mountSplit call, so they must be re-wrapped after
          // each mount — a one-time wrap is silently discarded on reopen.

          var origBusy = root.spsBusy;
          if (typeof origBusy === 'function') {
            root.spsBusy = function (stage) {
              if (stage === 0 && !st.holdStarted) {
                st.holdStarted = true;
                step('hold_started');
                emit('booking_hold_start', base);
              }
              if (stage === 1 && !st.cartStarted) {
                st.cartStarted = true;
                step('cart_write');
                onCartWriteStarted(st, base, root);
              }
              return origBusy.apply(this, arguments);
            };
          }

          var origError = root.spsError;
          if (typeof origError === 'function') {
            root.spsError = function (msg) {
              st.errored = true;
              emit('booking_error', Object.assign({}, base, {
                error_message: String(msg || '').slice(0, 100),
                error_stage: st.cartStarted ? 'cart_add' : (st.holdStarted ? 'seat_hold' : 'validation'),
                furthest_step: st.furthest
              }));
              return origError.apply(this, arguments);
            };
          }

          // Backdrop click and Esc close the <dialog> without ever calling
          // opts.onClose, so abandonment has to be caught on the dialog itself.
          var dlg = root.closest ? root.closest('dialog') : null;
          if (dlg) {
            dlg.addEventListener('close', function () { closeOut('dialog_close'); });
            dlg.addEventListener('cancel', function () { closeOut('esc_key'); });
          }
          window.addEventListener('pagehide', function () { closeOut('page_exit'); }, { once: true });
        } catch (e) {
          if (DEBUG) console.warn('[session-analytics] picker hooks failed', e);
        }

        return out;
      };
    });
  }

  function variantPrice(variants, id) {
    for (var i = 0; i < variants.length; i++) {
      if (String(variants[i].id) === String(id)) return variants[i].price_cents || 0;
    }
    return 0;
  }

  /**
   * The seat hold succeeded and the binder has started writing to /cart/add.js.
   *
   * We fire add_to_cart and begin_checkout here rather than on a confirmed cart
   * response, because the binder owns that promise and hard-navigates to
   * /checkout the instant it resolves — there is no hook in between, and an
   * event fired during navigation is usually lost.
   *
   * The trade: a cart write that fails after a successful hold is counted as an
   * add. That is rare (the risky step is the hold, which has already passed) and
   * it is corrected in the data by booking_error with error_stage=cart_add, so
   * the over-count is measurable rather than invisible.
   */
  function onCartWriteStarted(st, base, root) {
    // Read the cart so analytics-events.js can build a properly shaped item
    // from the real line, instead of us hand-rolling an ecommerce payload.
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var line = null;
        for (var i = cart.items.length - 1; i >= 0; i--) {
          if (cart.items[i].properties && cart.items[i].properties._session_id) { line = cart.items[i]; break; }
        }
        if (!line) return;
        document.dispatchEvent(new CustomEvent('theme:cart:add', {
          detail: { data: line }, bubbles: true
        }));
        emit('booking_added_to_cart', Object.assign({}, base, {
          value: money((line.final_price || line.price || 0) * (line.quantity || 1)),
          currency: currency(),
          seats: line.quantity || 1
        }));
      })
      .catch(function () { /* navigation probably won the race */ });
  }

  // ─── gifting ───────────────────────────────────────────────────────────────

  var giftState = null;

  function instrumentGift(KG) {
    if (!KG || KG.__ksInstrumented) return;
    KG.__ksInstrumented = true;

    var origOpen = KG.open;
    if (typeof origOpen !== 'function') return;

    KG.open = function (o) {
      var opt = o || {};
      var mode = opt.mode === 'value' ? 'value' : 'seat';
      giftState = {
        opened: Date.now(),
        mode: mode,
        submitted: false,
        furthest: 'open',
        base: {
          gift_mode: mode,
          gift_kind: opt.kind || '',
          item_id: opt.variantId ? String(opt.variantId) : '',
          item_name: opt.title || '',
          currency: currency()
        }
      };
      if (mode === 'value' && opt.amountCents) giftState.base.gift_amount = money(opt.amountCents);
      if (mode === 'seat' && opt.priceCents) giftState.base.unit_price = money(opt.priceCents);

      emit('gift_open', giftState.base);
      wireGiftDom();
      return origOpen.apply(this, arguments);
    };
  }

  var giftWired = false;

  function wireGiftDom() {
    if (giftWired) return;
    var dlg = document.getElementById('kg-modal');
    if (!dlg) return;
    giftWired = true;

    function visibleStep() {
      var panes = dlg.querySelectorAll('.kg__step');
      for (var i = 0; i < panes.length; i++) {
        if (!panes[i].hasAttribute('hidden')) return panes[i].getAttribute('data-step');
      }
      return null;
    }

    var STEP_NAME = { '0': 'amount', '1': 'recipient', '2': 'message', '3': 'delivery' };

    // Capture phase: the modal's own trigger router runs in capture and calls
    // stopPropagation, so a bubble-phase listener here would never fire.
    dlg.addEventListener('click', function (e) {
      if (!giftState) return;
      var t = e.target && e.target.closest ? e.target : null;
      if (!t) return;

      var cta = t.closest('#kg-cta');
      if (cta) {
        var s = visibleStep();
        giftState.furthest = STEP_NAME[s] || s || giftState.furthest;
        if (s === '3') { onGiftSubmit(dlg); }
        else { emit('gift_step_complete', Object.assign({}, giftState.base, { step: STEP_NAME[s] || s })); }
        return;
      }
      var amt = t.closest('[data-amt]');
      if (amt) {
        emit('gift_amount_select', Object.assign({}, giftState.base, {
          gift_amount: money(Number(amt.getAttribute('data-amt')) || 0),
          amount_type: 'preset'
        }));
        return;
      }
      var art = t.closest('#kg-arts [data-art], #kg-arts button');
      if (art) {
        emit('gift_art_select', Object.assign({}, giftState.base, {
          gift_art: art.getAttribute('data-art') || ''
        }));
        return;
      }
      var when = t.closest('[data-kg-when]');
      if (when) {
        emit('gift_delivery_select', Object.assign({}, giftState.base, {
          delivery_when: when.getAttribute('data-kg-when') || ''
        }));
        return;
      }
      var ch = t.closest('#kg-ch [data-ch], #kg-ch button');
      if (ch) {
        emit('gift_delivery_select', Object.assign({}, giftState.base, {
          delivery_channel: ch.getAttribute('data-ch') || ''
        }));
      }
    }, true);

    dlg.addEventListener('close', function () {
      if (!giftState || giftState.submitted) { giftState = null; return; }
      emit('gift_abandon', Object.assign({}, giftState.base, {
        furthest_step: giftState.furthest,
        dwell_ms: Date.now() - giftState.opened,
        dwell_bucket: dwellBucket(Date.now() - giftState.opened)
      }));
      giftState = null;
    });
  }

  /**
   * Final CTA. Three possible endings, and only one of them is already tracked:
   *
   *   A  seat gift on its own product page → form.requestSubmit() →
   *      product-form-drawer.js dispatches theme:cart:add → analytics-events.js
   *      already fires add_to_cart. We must NOT fire a second one.
   *   B  seat gift anywhere else → raw fetch to /cart/add.js, no event, then a
   *      hard redirect to /checkout. Invisible today.
   *   C  gift balance → POST to the external engine, bypasses the Shopify cart
   *      entirely, redirects off-domain. Invisible today.
   *
   * A and B are indistinguishable from outside at click time, so we watch for
   * theme:cart:add for a moment: if it arrives, path A handled it; if it does
   * not, we are on path B and fire our own.
   */
  function onGiftSubmit(dlg) {
    if (!giftState) return;
    giftState.submitted = true;

    var qty = Number((document.getElementById('kg-n') || {}).textContent) || 1;
    var hasMsg = !!(document.getElementById('kg-msg') || {}).value;
    var msgLen = ((document.getElementById('kg-msg') || {}).value || '').length;

    var params = Object.assign({}, giftState.base, {
      gift_seats: qty,
      has_message: hasMsg ? 'yes' : 'no',
      message_length: msgLen,
      // PII stays out: presence only, never the values themselves.
      has_recipient_email: (document.getElementById('kg-email') || {}).value ? 'yes' : 'no',
      has_sender_name: (document.getElementById('kg-from') || {}).value ? 'yes' : 'no',
      delivery_date_set: (document.getElementById('kg-date') || {}).value ? 'yes' : 'no',
      time_to_submit_ms: Date.now() - giftState.opened
    });

    if (giftState.mode === 'value') {
      // Path C — leaves for an off-domain checkout, so this is the last chance.
      emit('gift_voucher_purchase', Object.assign({}, params, {
        value: giftState.base.gift_amount || 0
      }));
      return;
    }

    emit('gift_submit', params);

    // Path A vs B
    var sawCartAdd = false;
    function onAdd() { sawCartAdd = true; }
    document.addEventListener('theme:cart:add', onAdd);

    setTimeout(function () {
      document.removeEventListener('theme:cart:add', onAdd);
      if (sawCartAdd) {
        if (DEBUG) console.log('[session-analytics] gift path A — add_to_cart already fired');
        return;
      }
      fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var line = null;
          for (var i = cart.items.length - 1; i >= 0; i--) {
            if (cart.items[i].properties && cart.items[i].properties._gift) { line = cart.items[i]; break; }
          }
          if (!line) return;
          document.dispatchEvent(new CustomEvent('theme:cart:add', {
            detail: { data: line }, bubbles: true
          }));
        })
        .catch(function () { /* redirect won the race */ });
    }, 700);
  }

  // ─── boot ──────────────────────────────────────────────────────────────────
  // Both globals are created by deferred/inline scripts, so poll briefly rather
  // than assuming load order. Stops after ~15s so a page without either system
  // costs nothing.

  var boots = 0;
  var bootTimer = setInterval(function () {
    if (window.SEPicker) instrumentPicker(window.SEPicker);
    if (window.KraftGiftModal) instrumentGift(window.KraftGiftModal);
    var done = (!window.SEPicker || window.SEPicker.__ksInstrumented) &&
               (!window.KraftGiftModal || window.KraftGiftModal.__ksInstrumented);
    if ((done && boots > 8) || ++boots > 60) clearInterval(bootTimer);
  }, 250);

  if (window.SEPicker) instrumentPicker(window.SEPicker);
  if (window.KraftGiftModal) instrumentGift(window.KraftGiftModal);
})();
